/**
 * Wire protocol for WebSocket communication.
 *
 * Two channels exist:
 *   - Node <-> Hub  (`NodeMessage` / `HubToNodeMessage`)
 *   - Browser <-> Hub (`BrowserMessage` / `HubToBrowserMessage`)
 *
 * All messages are JSON objects with a `type` discriminator.
 * Requests carry an optional `jobId` so progress + completion can be matched.
 *
 * Keep this file the single source of truth for both sides.
 */

import type {
  ServerLiveStats,
  MachineTelemetry,
  ConsoleLine,
  ServerDoc,
  BackupRecord,
} from './types.js';

// -------------------------------------------------------------------
// NODE  →  HUB
// -------------------------------------------------------------------

export type NodeToHubMessage =
  | NodeAuthMsg
  | NodeHeartbeatMsg
  | NodeTelemetryMsg
  | NodeServerStatsMsg
  | NodeConsoleMsg
  | NodeProgressMsg
  | NodeResponseMsg
  | NodeErrorMsg
  | NodeEventMsg;

export interface NodeAuthMsg {
  type: 'auth';
  machine_name: string;
  /** HMAC of `${machine_name}:${ts}` keyed by the per-machine secret. */
  signature: string;
  ts: number;
  agent_version: string;
}

export interface NodeHeartbeatMsg {
  type: 'heartbeat';
  ts: number;
}

export interface NodeTelemetryMsg {
  type: 'telemetry';
  payload: MachineTelemetry;
}

export interface NodeServerStatsMsg {
  type: 'server_stats';
  payload: ServerLiveStats;
}

export interface NodeConsoleMsg {
  type: 'console';
  payload: ConsoleLine;
}

export interface NodeProgressMsg {
  type: 'progress';
  jobId: string;
  progress: number;          // 0..100
  message?: string;
  /** optional sub-channel — used by update flow to forward steamcmd lines */
  console?: string;
}

export interface NodeResponseMsg {
  type: 'response';
  jobId: string;
  data: unknown;
}

export interface NodeErrorMsg {
  type: 'error';
  jobId?: string;
  message: string;
}

/** Unsolicited events - server crash detected, server restarted by heartbeat, etc. */
export interface NodeEventMsg {
  type: 'event';
  event:
    | 'server_crashed'
    | 'server_auto_restarted'
    | 'pid_changed'
    | 'agent_started'
    | 'agent_shutting_down'
    | 'agent_orphaned';
  Server_ID?: number;
  data?: Record<string, unknown>;
  ts: number;
}

// -------------------------------------------------------------------
// HUB  →  NODE
// -------------------------------------------------------------------

export type HubToNodeMessage =
  | HubAuthOkMsg
  | HubAuthFailMsg
  | HubHeartbeatAckMsg
  | HubRequestMsg
  | HubServerListMsg;

export interface HubAuthOkMsg {
  type: 'auth_ok';
  /** Server records this machine should manage, freshly fetched from DB. */
  servers: ServerDoc[];
}

export interface HubAuthFailMsg {
  type: 'auth_fail';
  reason: string;
}

export interface HubHeartbeatAckMsg {
  type: 'heartbeat_ack';
  ts: number;
}

/** Hub asks Node to do something. Node replies with progress + response or error. */
export interface HubRequestMsg {
  type: 'request';
  jobId: string;
  command: NodeCommand;
  args: Record<string, unknown>;
}

/** Pushed when server set for this machine changes, so the Node can re-cache. */
export interface HubServerListMsg {
  type: 'server_list';
  servers: ServerDoc[];
}

export type NodeCommand =
  | 'start'
  | 'soft_stop'
  | 'force_stop'
  | 'restart'
  | 'wipe'
  | 'update'
  | 'backup'
  | 'restore'
  | 'snapshot'
  | 'load_snapshot'
  | 'rcon'
  | 'upload_files'
  | 'provision'
  | 'deprovision'
  | 'fetch_log_tail';

// -------------------------------------------------------------------
// BROWSER  ←→  HUB
// -------------------------------------------------------------------

export type BrowserToHubMessage =
  | BrowserSubscribeMsg
  | BrowserUnsubscribeMsg
  | BrowserActionMsg
  | BrowserBulkActionMsg
  | BrowserBulkCancelMsg
  | BrowserPingMsg;

export interface BrowserSubscribeMsg {
  type: 'subscribe';
  /** Topics: 'all' | 'machine:<name>' | 'server:<id>' | 'console:<id>' */
  topics: string[];
}

export interface BrowserUnsubscribeMsg {
  type: 'unsubscribe';
  topics: string[];
}

export interface BrowserActionMsg {
  type: 'action';
  action_id: string;
  Server_ID: number;
  command: NodeCommand;
  args?: Record<string, unknown>;
}

export interface BrowserPingMsg {
  type: 'ping';
  ts: number;
}

/**
 * Run the same NodeCommand against many servers at once. Hub fans out per
 * machine in parallel but sequentially within each machine, so SteamCMD
 * can't trip over its own depot/manifest locks when many servers share a
 * single machine and a single SteamCMD install.
 */
export interface BrowserBulkActionMsg {
  type: 'bulk_action';
  bulk_id: string;
  /** Subset of NodeCommand that is meaningful in bulk. */
  command: 'start' | 'soft_stop' | 'force_stop' | 'restart' | 'update' | 'wipe' | 'backup';
  /** Which servers to target. */
  target:
    | { kind: 'all' }
    | { kind: 'group'; group: string }
    | { kind: 'machine'; machine: string }
    | { kind: 'servers'; ids: number[] };
  args?: Record<string, unknown>;
}

export interface BrowserBulkCancelMsg {
  type: 'bulk_cancel';
  bulk_id: string;
}

export type HubToBrowserMessage =
  | HubBrowserHelloMsg
  | HubBrowserPongMsg
  | HubBrowserSnapshotMsg
  | HubBrowserMachineTelemetryMsg
  | HubBrowserServerStatsMsg
  | HubBrowserNodeStatusMsg
  | HubBrowserConsoleMsg
  | HubBrowserActionAckMsg
  | HubBrowserActionProgressMsg
  | HubBrowserActionResultMsg
  | HubBrowserBulkStartedMsg
  | HubBrowserBulkServerProgressMsg
  | HubBrowserBulkDoneMsg
  | HubBrowserDataChangedMsg;

export interface HubBrowserHelloMsg {
  type: 'hello';
  user: { discord_id: string; discord_username: string; role: string };
  server_time: number;
}

export interface HubBrowserPongMsg {
  type: 'pong';
  ts: number;
}

/** Initial dump on subscribe — current state of everything subscribed to. */
export interface HubBrowserSnapshotMsg {
  type: 'snapshot';
  servers: ServerDoc[];
  live: Record<number, ServerLiveStats>;
  telemetry: Record<string, MachineTelemetry>;
  node_status: Record<string, { online: boolean; last_seen: number }>;
  backups?: BackupRecord[];
}

export interface HubBrowserMachineTelemetryMsg {
  type: 'machine_telemetry';
  payload: MachineTelemetry;
}

export interface HubBrowserServerStatsMsg {
  type: 'server_stats';
  payload: ServerLiveStats;
}

export interface HubBrowserNodeStatusMsg {
  type: 'node_status';
  machine_name: string;
  online: boolean;
  last_seen: number;
}

export interface HubBrowserConsoleMsg {
  type: 'console';
  payload: ConsoleLine;
}

export interface HubBrowserActionAckMsg {
  type: 'action_ack';
  action_id: string;
  jobId: string;
}

export interface HubBrowserActionProgressMsg {
  type: 'action_progress';
  action_id: string;
  progress: number;
  message?: string;
  console?: string;
}

export interface HubBrowserActionResultMsg {
  type: 'action_result';
  action_id: string;
  ok: boolean;
  data?: unknown;
  error?: string;
}

/** Bulk run accepted by the Hub. Includes the resolved server list so the UI
 *  can render the queue immediately instead of waiting for first per-server
 *  status events. */
export interface HubBrowserBulkStartedMsg {
  type: 'bulk_started';
  bulk_id: string;
  command: BrowserBulkActionMsg['command'];
  servers: Array<{ Server_ID: number; Server_Name: string; Server_Machine: string }>;
}

export type BulkServerStatus = 'queued' | 'running' | 'done' | 'error' | 'skipped' | 'cancelled';

export interface HubBrowserBulkServerProgressMsg {
  type: 'bulk_server_progress';
  bulk_id: string;
  Server_ID: number;
  status: BulkServerStatus;
  /** Underlying action progress 0-100 if the per-server command emits it. */
  progress?: number;
  message?: string;
  error?: string;
  /** Forwarded console line from the agent (steamcmd, oxide, etc.). */
  console?: string;
}

export interface HubBrowserBulkDoneMsg {
  type: 'bulk_done';
  bulk_id: string;
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
  cancelled: number;
}

/** Generic notification that a server doc changed (created, edited, deleted, map queue, etc.) */
export interface HubBrowserDataChangedMsg {
  type: 'data_changed';
  entity: 'server' | 'machine' | 'backup' | 'user';
  op: 'created' | 'updated' | 'deleted';
  id: number | string;
  data?: unknown;
}

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

export const PROTOCOL_VERSION = '2.0.0';

/** Wipe Extras applied by default to brand-new servers. Can be edited later. */
export const DEFAULT_WIPE_EXTRAS = [
  'oxide/data/StashWarning/spawns/',
  'oxide/data/Arkan/',
  'oxide/data/AutoPurge.json',
  'oxide/data/FurnaceSplitter.json',
  'oxide/data/StashWarning.json',
];
